import React from "react";
import { useLabels } from "../../../projectQueries";
import {
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  makeStyles,
  Theme,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import { getTheme } from "../../../../common/getLocalTheme";
import { NewLabel } from "../../../../common/NewLabelModal";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  styledChip: {
    backgroundColor: "#f3e8ff",
    color: "#8b5cf6",
    fontWeight: 600,
    fontSize: "0.875rem",
    borderRadius: 8,
    border: "none",
    height: "auto",
    padding: "3px 4px",
    "& .MuiChip-label": {
      paddingLeft: 8,
      paddingRight: 4,
      paddingTop: 2,
      paddingBottom: 2,
    },
    "& .MuiChip-deleteIcon": {
      color: "#8b5cf6",
      width: 16,
      height: 16,
      marginRight: 4,
      "&:hover": {
        color: "#7c3aed",
      },
    },
  },
  autocompleteInput: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: "#ffffff",
      paddingTop: "2px !important",
      paddingBottom: "2px !important",
      "& fieldset": {
        borderColor: "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: "#cbd5e1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#8b5cf6",
        borderWidth: "1.5px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "6px 8px",
      fontSize: "0.875rem",
      color: "#1e293b",
    },
  },
  addNewOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    color: "#8b5cf6",
    fontWeight: 600,
  },
}));

interface LabelItem {
  id: number;
  name: string;
  hex_color?: string;
  [key: string]: any;
}

interface LabelsSectionProps {
  usedLabels?: LabelItem[];
  setUsedLabels: React.Dispatch<React.SetStateAction<LabelItem[]>>;
  isNew?: boolean;
}

export const LabelsSection: React.FC<LabelsSectionProps> = ({
  usedLabels = [],
  setUsedLabels,
}) => {
  const classes = useStyles();
  const { data: labels, isLoading: isLoadingLabels } = useLabels();

  const [unusedLabels, setUnusedLabels] = React.useState<LabelItem[]>([]);
  const [open, setOpen] = React.useState(false);

  const handleAdd = (label: LabelItem) => {
    setUsedLabels((prev) => [...prev, label]);
  };

  const handleDelete = (labelToDelete: LabelItem) => {
    setUsedLabels((prev) =>
      prev.filter((label) => label.id !== labelToDelete.id)
    );
  };

  React.useEffect(() => {
    if (labels?.results) {
      const remaining = labels.results.filter(
        (label: LabelItem) =>
          !usedLabels.some((used) => used.id === label.id)
      );
      setUnusedLabels(remaining);
    } else if (labels && Array.isArray(labels)) {
      const remaining = labels.filter(
        (label: LabelItem) =>
          !usedLabels.some((used) => used.id === label.id)
      );
      setUnusedLabels(remaining);
    }
  }, [labels, usedLabels]);

  if (isLoadingLabels) {
    return <CircularProgress size={20} style={{ color: "#8b5cf6" }} />;
  }

  return (
    <Box className={classes.container}>
      {/* Active Labels Chips */}
      {usedLabels.length > 0 && (
        <Box className={classes.chipContainer}>
          {usedLabels.map((label: LabelItem) => (
            <Chip
              key={label.id}
              label={label.name}
              disabled={getTheme()}
              onDelete={() => handleDelete(label)}
              deleteIcon={<CloseIcon />}
              className={classes.styledChip}
              style={{
                backgroundColor: label.hex_color
                  ? `${label.hex_color}20`
                  : undefined,
                color: label.hex_color || undefined,
              }}
            />
          ))}
        </Box>
      )}

      {/* Autocomplete Input Selector */}
      <Autocomplete
        id="addLabels"
        options={[...(unusedLabels || []), { id: 0, name: "New Label" }]}
        getOptionLabel={(option: LabelItem) => option.name || ""}
        disabled={getTheme()}
        fullWidth
        onChange={(_, newValue) => {
          if (!newValue) return;

          if (newValue.id === 0) {
            setOpen(true);
            return;
          }

          handleAdd(newValue);
        }}
        renderOption={(option: LabelItem) => {
          if (option.id === 0) {
            return (
              <Typography className={classes.addNewOption}>
                <AddIcon style={{ fontSize: 18 }} /> Create New Label
              </Typography>
            );
          }

          return (
            <Chip
              size="small"
              label={option.name}
              style={{
                backgroundColor: option.hex_color
                  ? `${option.hex_color}20`
                  : "#f3e8ff",
                color: option.hex_color || "#8b5cf6",
                fontWeight: 600,
              }}
            />
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="+ Add a label"
            variant="outlined"
            className={classes.autocompleteInput}
          />
        )}
      />

      <NewLabel isOpen={open} setOpen={setOpen} />
    </Box>
  );
};